import React, { useState, useMemo } from 'react'
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts'
import { primaryDates } from '../../lib/primaryDates'

const AE_COLORS = {
  'Rande': '#2563eb',
  'Casey': '#dc2626',
  'Kiera': '#16a34a',
  'Lauren': '#9333ea',
  'Max': '#ea580c',
  'Paul': '#0891b2',
  'Rachel': '#ca8a04'
}

function GMPTimeline({ clients = [] }) {
  const [mode, setMode] = useState('per-date') // 'per-date' or 'running-total'
  const [pinnedDate, setPinnedDate] = useState(null)

  const GENERAL_ELECTION_DATE = '2026-11-03'

  // Build a map of state -> primary date
  const stateToPrimary = useMemo(() => {
    const map = {}
    primaryDates.forEach(p => {
      map[p.state] = p.primary
    })
    return map
  }, [])

  // Assign each client to both their primary date AND general election
  const clientsWithDates = useMemo(() => {
    const matched = clients.filter(c => c.state && stateToPrimary[c.state.toUpperCase()])

    const primaryEntries = matched.map(c => ({
      ...c,
      primaryDate: stateToPrimary[c.state.toUpperCase()],
      gmp: (c.linear_budget || 0) + (c.streaming_budget || 0),
      electionType: 'primary'
    }))

    const generalEntries = matched.map(c => ({
      ...c,
      primaryDate: GENERAL_ELECTION_DATE,
      gmp: (c.linear_budget || 0) + (c.streaming_budget || 0),
      electionType: 'general'
    }))

    return [...primaryEntries, ...generalEntries]
  }, [clients, stateToPrimary])

  // Get unique AEs that have at least one client with a primary date
  const activeAEs = useMemo(() => {
    const aes = new Set(clientsWithDates.map(c => c.account_executive).filter(Boolean))
    return [...aes].sort()
  }, [clientsWithDates])

  // Build a lookup: { "2026-03-03|Casey": [client, client, ...] }
  const clientLookup = useMemo(() => {
    const lookup = {}
    clientsWithDates.forEach(c => {
      if (!c.account_executive) return
      const key = `${c.primaryDate}|${c.account_executive}`
      if (!lookup[key]) lookup[key] = []
      lookup[key].push(c)
    })
    return lookup
  }, [clientsWithDates])

  // Get unique dates that have at least one client, sorted chronologically
  const uniqueDates = useMemo(() => {
    const dates = new Set(clientsWithDates.filter(c => c.account_executive).map(c => c.primaryDate))
    return [...dates].sort()
  }, [clientsWithDates])

  // Build chart data
  const chartData = useMemo(() => {
    const perDateData = uniqueDates.map(date => {
      const row = {
        date,
        dateLabel: new Date(date + 'T00:00:00').toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric'
        })
      }
      activeAEs.forEach(ae => {
        const key = `${date}|${ae}`
        const aeClients = clientLookup[key] || []
        row[ae] = aeClients.reduce((sum, c) => sum + c.gmp, 0)
      })
      return row
    })

    if (mode === 'per-date') return perDateData

    // Running total
    const runningTotals = {}
    activeAEs.forEach(ae => { runningTotals[ae] = 0 })

    return perDateData.map(row => {
      const newRow = { date: row.date, dateLabel: row.dateLabel }
      activeAEs.forEach(ae => {
        runningTotals[ae] += row[ae]
        newRow[ae] = runningTotals[ae]
      })
      return newRow
    })
  }, [uniqueDates, activeAEs, clientLookup, mode])

  const formatCurrency = (amount) => {
    if (amount >= 1000000) return `$${(amount / 1000000).toFixed(1)}M`
    if (amount >= 1000) return `$${(amount / 1000).toFixed(0)}K`
    return `$${amount}`
  }

  const formatFullCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0)
  }

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload || payload.length === 0) return null

    // Get the original date from the first payload entry
    const dataPoint = payload[0]?.payload
    const date = dataPoint?.date

    // Find the state(s) for this date
    const statesOnDate = primaryDates
      .filter(p => p.primary === date)
      .map(p => `${p.name} (${p.state})`)

    return (
      <div style={{
        backgroundColor: 'white',
        border: '1px solid #e5e7eb',
        borderRadius: '8px',
        padding: '16px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        maxWidth: '380px',
        maxHeight: '400px',
        overflowY: 'auto'
      }}>
        <div style={{ fontWeight: '600', fontSize: '14px', color: '#111827', marginBottom: '4px' }}>
          {new Date(date + 'T00:00:00').toLocaleDateString('en-US', {
            weekday: 'short',
            month: 'long',
            day: 'numeric',
            year: 'numeric'
          })}
        </div>
        <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '12px' }}>
          {statesOnDate.join(', ')}
        </div>

        {payload
          .filter(entry => {
            // In per-date mode, show AEs that have clients on this date (even $0)
            // In running-total mode, show AEs with value > 0
            if (mode === 'per-date') {
              const key = `${date}|${entry.dataKey}`
              return clientLookup[key] && clientLookup[key].length > 0
            }
            return entry.value > 0
          })
          .sort((a, b) => b.value - a.value)
          .map(entry => {
            const ae = entry.dataKey
            const key = `${date}|${ae}`
            const aeClients = clientLookup[key] || []

            return (
              <div key={ae} style={{ marginBottom: '12px' }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '4px'
                }}>
                  <span style={{
                    fontWeight: '600',
                    fontSize: '13px',
                    color: entry.color,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}>
                    <span style={{
                      width: '8px',
                      height: '8px',
                      borderRadius: '50%',
                      backgroundColor: entry.color,
                      display: 'inline-block'
                    }} />
                    {ae}
                  </span>
                  <span style={{
                    fontWeight: '600',
                    fontSize: '13px',
                    color: '#111827',
                    fontFamily: 'monospace'
                  }}>
                    {formatFullCurrency(entry.value)}
                    {mode === 'running-total' && aeClients.length > 0 && (
                      <span style={{ color: '#6b7280', fontWeight: '400', fontSize: '11px', marginLeft: '4px' }}>
                        (+{formatFullCurrency(aeClients.reduce((s, c) => s + c.gmp, 0))})
                      </span>
                    )}
                  </span>
                </div>
                {aeClients.length > 0 && (
                  <div style={{ paddingLeft: '14px' }}>
                    {aeClients.map((client, idx) => (
                      <div key={idx} style={{
                        fontSize: '12px',
                        color: '#6b7280',
                        display: 'flex',
                        justifyContent: 'space-between',
                        padding: '2px 0'
                      }}>
                        <span>
                          {client.client_name}
                          {client.race ? ` — ${client.race}` : ''}
                          {client.state ? ` (${client.state})` : ''}
                        </span>
                        <span style={{
                          fontFamily: 'monospace',
                          marginLeft: '12px',
                          color: client.gmp === 0 ? '#ef4444' : '#374151'
                        }}>
                          {client.gmp === 0 ? '$0 ⚠' : formatFullCurrency(client.gmp)}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )
          })
        }
      </div>
    )
  }

  if (uniqueDates.length === 0) {
    return (
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '40px',
        textAlign: 'center',
        color: '#6b7280',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
      }}>
        No clients with assigned states and account executives to chart.
      </div>
    )
  }

  return (
    <div style={{
      backgroundColor: 'white',
      borderRadius: '12px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      padding: '24px'
    }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '24px'
      }}>
        <div>
          <h2 style={{ margin: '0 0 4px 0', fontSize: '20px', fontWeight: '600', color: '#111827' }}>
            GMP by Account Executive
          </h2>
          <p style={{ margin: 0, fontSize: '14px', color: '#6b7280' }}>
            {mode === 'per-date' ? 'Budget per primary date' : 'Cumulative budget over primary calendar'}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            onClick={() => setMode('per-date')}
            style={{
              padding: '6px 14px',
              borderRadius: '6px',
              border: 'none',
              backgroundColor: mode === 'per-date' ? '#111827' : '#f3f4f6',
              color: mode === 'per-date' ? 'white' : '#374151',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: '500'
            }}
          >
            Per Date
          </button>
          <button
            onClick={() => setMode('running-total')}
            style={{
              padding: '6px 14px',
              borderRadius: '6px',
              border: 'none',
              backgroundColor: mode === 'running-total' ? '#111827' : '#f3f4f6',
              color: mode === 'running-total' ? 'white' : '#374151',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: '500'
            }}
          >
            Running Total
          </button>
        </div>
      </div>

      <ResponsiveContainer width="100%" height={420}>
        <LineChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>        
        <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
          <XAxis
            dataKey="dateLabel"
            tick={{ fontSize: 12, fill: '#6b7280' }}
            tickLine={false}
            axisLine={{ stroke: '#e5e7eb' }}
          />
          <YAxis
            tickFormatter={formatCurrency}
            tick={{ fontSize: 12, fill: '#6b7280' }}
            tickLine={false}
            axisLine={{ stroke: '#e5e7eb' }}
            width={70}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend
            wrapperStyle={{ fontSize: '13px', paddingTop: '12px' }}
          />
          {activeAEs.map(ae => (
            <Line
              key={ae}
              type="monotone"
              dataKey={ae}
              stroke={AE_COLORS[ae] || '#6b7280'}
              strokeWidth={2}
              dot={(props) => {
                const { cx, cy, payload } = props
                if (cx == null || cy == null) return null
                return (
                  <circle
                    cx={cx}
                    cy={cy}
                    r={5}
                    fill={AE_COLORS[ae] || '#6b7280'}
                    stroke="white"
                    strokeWidth={2}
                    style={{ cursor: 'pointer' }}
                    onClick={(e) => {
                      e.stopPropagation()
                      setPinnedDate(prev => prev === payload.date ? null : payload.date)
                    }}
                  />
                )
              }}
              activeDot={(props) => {
                const { cx, cy, payload } = props
                return (
                  <circle
                    cx={cx}
                    cy={cy}
                    r={7}
                    fill={AE_COLORS[ae] || '#6b7280'}
                    stroke="white"
                    strokeWidth={2}
                    style={{ cursor: 'pointer' }}
                    onClick={(e) => {
                      e.stopPropagation()
                      setPinnedDate(prev => prev === payload.date ? null : payload.date)
                    }}
                  />
                )
              }}
              connectNulls={false}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
      {/* Pinned Detail Panel */}
      {pinnedDate && (() => {
        const dataRow = chartData.find(d => d.date === pinnedDate)
        if (!dataRow) return null

        const statesOnDate = pinnedDate === GENERAL_ELECTION_DATE
          ? ['General Election']
          : primaryDates.filter(p => p.primary === pinnedDate).map(p => `${p.name} (${p.state})`)

        const aeEntries = activeAEs
          .map(ae => {
            const key = `${pinnedDate}|${ae}`
            const aeClients = clientLookup[key] || []
            const value = dataRow[ae] || 0
            return { ae, value, aeClients }
          })
          .filter(entry => {
            if (mode === 'per-date') return entry.aeClients.length > 0
            return entry.value > 0
          })
          .sort((a, b) => b.value - a.value)

        return (
          <div style={{
            marginTop: '24px',
            border: '1px solid #e5e7eb',
            borderRadius: '12px',
            overflow: 'hidden'
          }}>
            <div style={{
              padding: '16px 20px',
              backgroundColor: '#f9fafb',
              borderBottom: '1px solid #e5e7eb',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <div>
                <div style={{ fontWeight: '600', fontSize: '16px', color: '#111827' }}>
                  {new Date(pinnedDate + 'T00:00:00').toLocaleDateString('en-US', {
                    weekday: 'long',
                    month: 'long',
                    day: 'numeric',
                    year: 'numeric'
                  })}
                </div>
                <div style={{ fontSize: '13px', color: '#6b7280', marginTop: '2px' }}>
                  {statesOnDate.join(', ')}
                </div>
              </div>
              <button
                onClick={() => setPinnedDate(null)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '20px',
                  color: '#9ca3af',
                  cursor: 'pointer',
                  padding: '4px 8px',
                  lineHeight: 1
                }}
              >
                ✕
              </button>
            </div>

            <div style={{ padding: '16px 20px', maxHeight: '400px', overflowY: 'auto' }}>
              {aeEntries.map(({ ae, value, aeClients }) => (
                <div key={ae} style={{
                  marginBottom: '16px',
                  paddingBottom: '16px',
                  borderBottom: '1px solid #f3f4f6'
                }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '8px'
                  }}>
                    <span style={{
                      fontWeight: '600',
                      fontSize: '14px',
                      color: AE_COLORS[ae] || '#6b7280',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <span style={{
                        width: '10px',
                        height: '10px',
                        borderRadius: '50%',
                        backgroundColor: AE_COLORS[ae] || '#6b7280',
                        display: 'inline-block'
                      }} />
                      {ae}
                    </span>
                    <span style={{
                      fontWeight: '600',
                      fontSize: '14px',
                      color: '#111827',
                      fontFamily: 'monospace'
                    }}>
                      {formatFullCurrency(value)}
                      {mode === 'running-total' && aeClients.length > 0 && (
                        <span style={{ color: '#6b7280', fontWeight: '400', fontSize: '12px', marginLeft: '6px' }}>
                          (+{formatFullCurrency(aeClients.reduce((s, c) => s + c.gmp, 0))})
                        </span>
                      )}
                    </span>
                  </div>
                  {aeClients.map((client, idx) => (
                    <div key={idx} style={{
                      fontSize: '13px',
                      color: '#6b7280',
                      display: 'flex',
                      justifyContent: 'space-between',
                      padding: '4px 0 4px 18px'
                    }}>
                      <span>
                        {client.client_name}
                        {client.race ? ` — ${client.race}` : ''}
                        {client.state ? ` (${client.state})` : ''}
                      </span>
                      <span style={{
                        fontFamily: 'monospace',
                        marginLeft: '12px',
                        color: client.gmp === 0 ? '#ef4444' : '#374151',
                        fontWeight: client.gmp === 0 ? '600' : '400'
                      }}>
                        {client.gmp === 0 ? '$0 ⚠' : formatFullCurrency(client.gmp)}
                      </span>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        )
      })()}
    </div>
  )
}

export default GMPTimeline